import React from 'react';
import usePageMeta from '../../hooks/usePageMeta';
import { EMPRESA } from './empresa';
import './Legal.css';

interface TableRow {
  cells: string[];
}

const LegalTable: React.FC<{ headers: string[]; rows: TableRow[] }> = ({ headers, rows }) => (
  <div className="legal-table-wrapper">
    <table className="legal-table">
      <thead>
        <tr>
          {headers.map((header) => (
            <th key={header}>{header}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, index) => (
          <tr key={index}>
            {row.cells.map((cell, cellIndex) => (
              <td key={cellIndex}>{cell}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const Privacidade: React.FC = () => {
  usePageMeta({
    title: 'Política de Privacidade',
    description:
      'Política de privacidade da UpdateCommit NFSe conforme a LGPD: dados coletados, finalidades, retenção, compartilhamento e direitos do titular.',
    path: '/privacidade',
  });

  return (
    <div className="legal-page">
      <header className="legal-header">
        <h1 className="legal-title">Política de Privacidade — UpdateCommit NFSe</h1>
        <p className="legal-meta">
          Versão 1.0 — Junho/2026 · Vigência: a partir de 01/07/2026 · Conforme a Lei Geral de
          Proteção de Dados (LGPD — Lei 13.709/2018)
        </p>
      </header>

      <h2>1. Controlador dos dados</h2>
      <p>
        <strong>{EMPRESA.razaoSocial}</strong>
        <br />
        CNPJ: {EMPRESA.cnpj}
        <br />
        Endereço: {EMPRESA.endereco}
        <br />
        E-mail do Encarregado (DPO):{' '}
        <a href={`mailto:${EMPRESA.emailPrivacidade}`}>{EMPRESA.emailPrivacidade}</a>
      </p>

      <h2>2. Dados coletados e finalidades</h2>

      <h3>2.1 Dados de cadastro</h3>
      <LegalTable
        headers={['Dado', 'Finalidade', 'Base legal (LGPD)']}
        rows={[
          { cells: ['Nome completo / razão social', 'Identificação do Usuário', 'Art. 7, V (execução de contrato)'] },
          { cells: ['E-mail', 'Autenticação, notificações, suporte', 'Art. 7, V'] },
          { cells: ['Senha (hash bcrypt)', 'Autenticação', 'Art. 7, V'] },
          { cells: ['CNPJ / CPF', 'Emissão de NFS-e, faturamento', 'Art. 7, V'] },
        ]}
      />

      <h3>2.2 Dados fiscais da organização</h3>
      <LegalTable
        headers={['Dado', 'Finalidade', 'Base legal']}
        rows={[
          {
            cells: [
              'CNPJ, Inscrição Municipal, código IBGE',
              'Emissão de NFS-e conforme legislação tributária',
              'Art. 7, II (cumprimento de obrigação legal)',
            ],
          },
          {
            cells: [
              'Certificado digital PFX + senha',
              'Assinatura XMLDSIG e mTLS com a SEFAZ (criptografados com AES-256-GCM)',
              'Art. 7, V',
            ],
          },
        ]}
      />

      <h3>2.3 Dados dos tomadores de serviço (clientes)</h3>
      <LegalTable
        headers={['Dado', 'Finalidade', 'Base legal']}
        rows={[
          {
            cells: [
              'Nome, CPF/CNPJ, e-mail, telefone',
              'Preenchimento da NFS-e, envio de e-mail com a nota',
              'Art. 7, V',
            ],
          },
        ]}
      />
      <p>
        O Usuário (prestador) atua como controlador dos dados de seus tomadores perante a LGPD. A
        UpdateCommit atua como operadora conforme o Art. 39.
      </p>

      <h3>2.4 Dados de uso e operação</h3>
      <LegalTable
        headers={['Dado', 'Finalidade', 'Base legal']}
        rows={[
          {
            cells: [
              'Logs de auditoria (IP, ação, timestamp)',
              'Segurança, prevenção de fraude, conformidade',
              'Art. 7, IX (interesse legítimo)',
            ],
          },
          { cells: ['Logs de erro operacional', 'Diagnóstico e melhoria do serviço', 'Art. 7, IX'] },
        ]}
      />

      <h3>2.5 Dados de faturamento</h3>
      <p>
        O pagamento é processado diretamente pela <strong>Stripe, Inc.</strong> A UpdateCommit
        recebe apenas confirmações de pagamento; dados de cartão nunca trafegam pelos servidores da
        UpdateCommit.
      </p>

      <h2>3. Retenção de dados</h2>
      <LegalTable
        headers={['Categoria', 'Período de retenção']}
        rows={[
          {
            cells: [
              'Dados de cadastro e NFS-e emitidas',
              'Durante a vigência do contrato + 5 anos (obrigação legal tributária)',
            ],
          },
          {
            cells: [
              'Certificados digitais',
              'Até exclusão manual pelo Usuário ou encerramento da conta',
            ],
          },
          { cells: ['Logs de auditoria', '5 anos'] },
          { cells: ['Dados de clientes (tomadores)', 'Durante a vigência do contrato + 5 anos'] },
          { cells: ['Conta encerrada', '90 dias para exportação, depois exclusão permanente'] },
        ]}
      />

      <h2>4. Compartilhamento de dados</h2>
      <p>A UpdateCommit não vende nem compartilha dados pessoais com terceiros, exceto:</p>
      <ul>
        <li>
          <strong>SEFAZ Nacional / Receita Federal:</strong> dados da NFS-e transmitidos por
          obrigação legal.
        </li>
        <li>
          <strong>Stripe, Inc.:</strong> dados de pagamento, conforme a Política de Privacidade da
          Stripe.
        </li>
        <li>
          <strong>Provedores de infraestrutura (hosting):</strong> servidores localizados no
          Brasil; dados não transferidos ao exterior.
        </li>
        <li>
          <strong>Determinação judicial ou legal:</strong> quando exigido por autoridade
          competente.
        </li>
      </ul>

      <h2>5. Direitos do titular</h2>
      <p>Conforme a LGPD, o Usuário tem direito a:</p>
      <ul>
        <li>
          <strong>Confirmação e acesso:</strong> saber quais dados estão armazenados.
        </li>
        <li>
          <strong>Correção:</strong> atualizar dados incompletos ou incorretos.
        </li>
        <li>
          <strong>Anonimização, bloqueio ou eliminação:</strong> para dados desnecessários ou
          tratados em desconformidade.
        </li>
        <li>
          <strong>Portabilidade:</strong> exportar os próprios dados em formato estruturado.
        </li>
        <li>
          <strong>Revogação do consentimento:</strong> quando aplicável.
        </li>
        <li>
          <strong>Petição à ANPD:</strong> caso considere que seus direitos foram violados.
        </li>
      </ul>
      <p>
        Para exercer esses direitos, envie e-mail para{' '}
        <a href={`mailto:${EMPRESA.emailPrivacidade}`}>{EMPRESA.emailPrivacidade}</a> com o assunto{' '}
        <strong>"Direitos LGPD"</strong>.
      </p>

      <h2>6. Segurança</h2>
      <ul>
        <li>Senhas armazenadas como hash bcrypt.</li>
        <li>Certificados PFX criptografados com AES-256-GCM antes de armazenamento.</li>
        <li>Comunicação com a SEFAZ via mTLS (TLS 1.2).</li>
        <li>Acesso administrativo restrito por IP e autenticação multifator.</li>
        <li>Registro de auditoria imutável de todas as ações sensíveis.</li>
        <li>Testes de segurança e varredura de vulnerabilidades periódicos.</li>
      </ul>

      <h2>7. Cookies e rastreamento</h2>
      <p>
        A plataforma não usa cookies de rastreamento de terceiros. Apenas cookies de sessão
        próprios são utilizados para manter o usuário autenticado.
      </p>

      <h2>8. Atualizações desta política</h2>
      <p>
        Mudanças serão comunicadas por e-mail com antecedência mínima de 15 dias. O uso continuado
        após a data de vigência implica aceite da nova versão.
      </p>

      <h2>9. Contato</h2>
      <p>
        <strong>Encarregado de Dados (DPO):</strong> {EMPRESA.dpo}
        <br />
        <strong>E-mail:</strong>{' '}
        <a href={`mailto:${EMPRESA.emailPrivacidade}`}>{EMPRESA.emailPrivacidade}</a>
        <br />
        <strong>Endereço:</strong> {EMPRESA.endereco}
      </p>
    </div>
  );
};

export default Privacidade;
